import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { faqItems } from '@/mocks/navigation';
import { useState } from 'react';

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

const milestones = [
  { year: '2025', title: '项目启动', desc: '91导航项目正式立项，团队开始规划产品定位和核心功能' },
  { year: '2025 Q2', title: '内测上线', desc: '首个内测版本发布，收录首批100个优质网站' },
  { year: '2025 Q3', title: '正式发布', desc: '91导航正式对外发布，开放给所有用户使用' },
  { year: '2026', title: '快速成长', desc: '月访问量突破10万，收录网站超过500个，覆盖12个分类' },
];

const stats = [
  { value: '12', label: '分类覆盖', icon: 'ri-stack-line' },
  { value: '50+', label: '收录网站', icon: 'ri-global-line' },
  { value: '10万+', label: '月访问量', icon: 'ri-user-line' },
  { value: '99.9%', label: '服务可用率', icon: 'ri-shield-check-line' },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于91导航',
    description: '91导航是一站式网址导航与资源聚合平台，致力于帮助用户高效上网',
    url: 'https://91daohang.com/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91导航',
    url: 'https://91daohang.com',
    description: '一站式网址导航与资源聚合平台',
    foundingDate: '2025',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@91daohang.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <Header />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <section className="py-8 md:py-12">
            <div className="max-w-3xl">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">关于91导航</h1>
              <p className="text-lg text-foreground-600 leading-relaxed mb-6">
                91导航是一站式网址导航与资源聚合平台，致力于帮助每一位互联网用户高效上网。
                我们相信，好的上网体验应该从简单开始 —— 无需繁琐的搜索和输入，一键直达你需要的网站。
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mt-10">
              {stats.map((stat) => (
                <div key={stat.label} className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-900">{stat.value}</div>
                  <div className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-10 md:py-14 border-t border-background-200/70">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">品牌故事</h2>
            <p className="text-sm text-foreground-600 mb-8">从构想到现实，91导航的成长历程</p>

            <div className="max-w-3xl space-y-8">
              {milestones.map((m, i) => (
                <div key={i} className="flex gap-4 md:gap-6">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    {i < milestones.length - 1 && <div className="w-0.5 flex-1 bg-background-200 mt-1.5"></div>}
                  </div>
                  <div className="pb-8">
                    <span className="text-xs font-medium text-primary-600 bg-primary-100 px-2.5 py-0.5 rounded-full">{m.year}</span>
                    <h3 className="text-base font-semibold text-foreground-900 mt-2 mb-1">{m.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-10 md:py-14 border-t border-background-200/70">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">我们的承诺</h2>
            <p className="text-sm text-foreground-600 mb-8">以用户为中心，坚持品质与安全的核心理念</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              <div className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-11 h-11 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-accent-600 text-xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">严格筛选</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  每个收录的网站都经过编辑团队多维度评估，确保内容质量、安全性和实用性，
                  为用户提供最可靠的网站推荐。
                </p>
              </div>

              <div className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-11 h-11 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-refresh-line text-accent-600 text-xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">持续更新</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们持续关注互联网的动态变化，定期更新网站列表，新增优质网站，淘汰失效链接，
                  确保导航信息的时效性。
                </p>
              </div>

              <div className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-11 h-11 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-user-heart-line text-accent-600 text-xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">用户至上</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  用户的体验是我们最关心的事情。简洁、快速、准确是91导航的产品哲学，
                  让每一位用户都能享受到高效的上网体验。
                </p>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 border-t border-background-200/70">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">联系方式</h2>
            <p className="text-sm text-foreground-600 mb-8">如有任何问题、建议或合作意向，欢迎通过以下方式联系我们</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <a href="mailto:contact@91daohang.com" className="text-sm text-primary-600 hover:underline cursor-pointer">contact@91daohang.com</a>
                  <p className="text-xs text-foreground-500 mt-1">工作日24小时内回复</p>
                </div>
              </div>

              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-chat-smile-2-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">在线反馈</h3>
                  <p className="text-sm text-foreground-600">通过邮件发送反馈建议</p>
                  <p className="text-xs text-foreground-500 mt-1">我们认真对待每一条反馈</p>
                </div>
              </div>

              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shake-hands-line text-secondary-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">商务合作</h3>
                  <a href="mailto:business@91daohang.com" className="text-sm text-primary-600 hover:underline cursor-pointer">business@91daohang.com</a>
                  <p className="text-xs text-foreground-500 mt-1">期待与您合作</p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 border-t border-background-200/70">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">常见问题</h2>
            <p className="text-sm text-foreground-600 mb-8">以下是用户最常问的一些问题</p>

            <div className="max-w-3xl space-y-3">
              {faqItems.map((item, i) => (
                <div key={i} className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{item.question}</span>
                    <i className={`text-foreground-400 text-lg transition-transform flex-shrink-0 ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </button>
                  {openFaq === i && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </>
  );
}